/**
 * Gallery Wall Frame Mock Component
 *
 * Renders a single framed artwork on a neutral background using canvas.
 * Used in the gallery wall media gallery to show individual artwork/frame combos.
 *
 * Frame rendering delegated to shared frame-rendering.js module.
 */

// Shared rendering functions from frame-rendering.js (loaded via window global).
// Lazy-resolved to avoid race conditions when both modules load in parallel.
const getFrameRendering = () => window.FrameRendering;
const loadImage = (...args) => getFrameRendering().loadImage(...args);
const renderWallTexture = (...args) => getFrameRendering().renderWallTexture(...args);
const renderSpotlightWash = (...args) => getFrameRendering().renderSpotlightWash(...args);
const renderAmbientBleed = (...args) => getFrameRendering().renderAmbientBleed(...args);
const renderArtPrintFrame = (...args) => getFrameRendering().renderArtPrintFrame(...args);
const renderCanvasProductFrame = (...args) => getFrameRendering().renderCanvasProductFrame(...args);

// ── Shared offscreen canvas for corner-crop tiles ──
// All corner-crop tiles render one-at-a-time (staggered queue), so they share
// a single offscreen buffer instead of each allocating ~18MB of GPU memory.
// After rendering, the crop region is blitted to the tile's display canvas.
let _sharedOffscreen = null;
let _sharedOffscreenCtx = null;

function _getSharedOffscreen(size) {
  if (!_sharedOffscreen) {
    _sharedOffscreen = document.createElement('canvas');
    _sharedOffscreenCtx = _sharedOffscreen.getContext('2d');
  }
  if (_sharedOffscreen.width !== size || _sharedOffscreen.height !== size) {
    _sharedOffscreen.width = size;
    _sharedOffscreen.height = size;
  }
  return { canvas: _sharedOffscreen, ctx: _sharedOffscreenCtx };
}

// ── Shared wall background cache for corner-crop tiles ──
// All tiles share the same wall color, texture, and lighting map, so we render
// the wall background once and blit from the cache. Invalidated on wall color change.
let _wallCache = null;  // { canvas, ctx, size, wallColor, lightKey }

function _getWallCache(size, wallColor, textureTile, light, lightingMap, orientKey) {
  // textureTile identity must participate in the key: if any instance fills
  // the cache before the wall texture finishes loading (textureTile null),
  // every later tile would blit the texture-less wall until the next colour
  // change invalidated it.
  const lightKey = (light?.lightPos?.join(',') || '') + ':' + (orientKey || '')
    + ':' + (textureTile ? (textureTile.src || 'tile') : 'none');
  if (_wallCache && _wallCache.size === size && _wallCache.wallColor === wallColor && _wallCache.lightKey === lightKey) {
    return _wallCache.canvas;
  }
  if (!_wallCache) {
    _wallCache = { canvas: document.createElement('canvas'), size: 0, wallColor: '', lightKey: '' };
  }
  _wallCache.canvas.width = size;
  _wallCache.canvas.height = size;
  _wallCache.size = size;
  _wallCache.wallColor = wallColor;
  _wallCache.lightKey = lightKey;
  const ctx = _wallCache.canvas.getContext('2d');
  renderWallTexture(ctx, size, size, wallColor, textureTile, light, lightingMap);
  return _wallCache.canvas;
}

function _invalidateWallCache() {
  if (_wallCache) _wallCache.size = 0;
  _cornerBitmapCache.clear();
}

// ── Corner-crop bitmap cache ──
// Stores the fully-rendered corner thumbnail canvas keyed by everything that
// changes the rendered frame. Shared across all instances so the all-frames
// drawer and recommended row reuse each other's work.
const _cornerBitmapCache = new Map(); // _cornerCacheKey(el) → HTMLCanvasElement

function _cornerCacheKey(el) {
  // Moulding tile + width must be part of the key: two mouldings can share a
  // frame colour (e.g. oak flat vs oak ogee), and a colour-only key made every
  // same-colour card in the frame drawer blit the FIRST profile's bitmap —
  // showing a different physical moulding than the card sells.
  return `${el.dataset.frameColor || ''}:${el.dataset.borderOption || 'no-border'}:${el.dataset.productType || 'art_print'}:${el.dataset.mouldingTile || ''}:${el.dataset.mouldingWidth || ''}`;
}

// The corner-crop bitmap cache is keyed only by frame colour / border / product
// type — it assumes a single artwork shared by every tile on the page (true for
// a standard art PDP). When that shared artwork changes (e.g. the personalise
// PDP pushes a fresh live SVG preview into every corner mock via setArtwork),
// the cached bitmaps still hold the previous image, so render() would keep
// blitting the stale artwork. Track the artwork the cache was built for and
// drop every bitmap the first time it changes, so tiles re-render with the new
// artwork (cross-tile reuse for the new artwork is then rebuilt as usual).
let _cornerArtworkUrl = null;
function _setCornerArtwork(url) {
  if (_cornerArtworkUrl === url) return;
  _cornerArtworkUrl = url;
  _cornerBitmapCache.clear();
}

// ── Staggered render queue for corner-crop tiles ──
// Renders one tile per animation frame so multiple tiles don't block the same frame.
const _renderQueue = new Set();
let _renderRafId = 0;

function _processRenderQueue() {
  _renderRafId = 0;
  const first = _renderQueue.values().next().value;
  if (first) {
    _renderQueue.delete(first);
    first._pendingRender = false;
    try {
      first.render();
    } catch (err) {
      console.error('[gallery-wall-frame-mock] render() failed:', err);
    }
  }
  if (_renderQueue.size > 0) {
    _renderRafId = requestAnimationFrame(_processRenderQueue);
  }
}

function _enqueueRender(el) {
  _renderQueue.add(el);
  el._pendingRender = true;
  if (!_renderRafId) {
    _renderRafId = requestAnimationFrame(_processRenderQueue);
  }
}

function _dequeueRender(el) {
  _renderQueue.delete(el);
  el._pendingRender = false;
}

// Per-wall lighting config:
//   lightPos:      [x%, y%] of canvas — where the spotlight wash is centered
//   spotAlpha:     peak opacity of the final spotlight wash (over wall+frame+art)
//   shadowDir:     [x, y] multipliers for drop shadow offset (relative to shadow base)
//   lightingAlpha: opacity for the room lighting map multiply blend (0-1)
//   textureAlpha:  scalar for wall texture intensity (0-1, default 1)
const WALL_LIGHT_DEFAULT = { lightPos: [0.40, 0.22], spotAlpha: 0.04, shadowDir: [0.4, 0.6], lightingAlpha: 0.7 };
const WALL_LIGHT_MOODY   = { lightPos: [0.42, 0.28], spotAlpha: 0.07, shadowDir: [0.6, 0.8], lightingAlpha: 0.8 };

const WALL_COLORS = [
  { name: 'White',      hex: '#f5f0e8', light: WALL_LIGHT_DEFAULT },
  { name: 'French Grey', hex: '#b5b5ad', light: WALL_LIGHT_DEFAULT },
  { name: 'Lichen',      hex: '#BABCAA', light: WALL_LIGHT_DEFAULT },
  { name: 'Terracotta', hex: '#CA8160', light: { lightPos: [0.38, 0.24], spotAlpha: 0.05, shadowDir: [0.5, 0.7], lightingAlpha: 0.7 } },
  { name: 'Lemon',      hex: '#e8d497', light: { lightPos: [0.42, 0.18], spotAlpha: 0.03, shadowDir: [0.3, 0.7], lightingAlpha: 0.65, textureAlpha: 0.5 } },
  { name: 'Dark Blue',  hex: '#4a5468', light: WALL_LIGHT_MOODY }
];

const WALL_COLOR_STORAGE_KEY = 'gallery-wall-color';

class GalleryWallFrameMock extends HTMLElement {
  constructor() {
    super();
    this.canvas = null;
    this.ctx = null;
    this.artworkImage = null;
    this.mouldingTexture = null;
    this.wallTextureTile = null;
    this.roomLightingMaps = {};
    this.resizeObserver = null;
    this._resizeRafId = null;
    this._assetsLoaded = false;
    this._lastRenderedSize = 0;
    this.wallColor = WALL_COLORS[0].hex;
    this.wallLight = WALL_COLORS[0].light;
    this._wallPills = null;
    this._pickerOpen = false;
    this._thumbnailTargets = [];
  }

  /** Register a <canvas> element to receive a scaled blit after every render. */
  addThumbnailTarget(canvas) {
    if (canvas && !this._thumbnailTargets.includes(canvas)) {
      this._thumbnailTargets.push(canvas);
    }
  }

  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    this.parseData();

    // Restore saved wall color (localStorage > data attribute > default)
    const saved = this._getSavedWallColor();
    if (saved) {
      this.wallColor = saved.hex;
      this.wallLight = saved.light;
    } else if (this.dataset.wallColor) {
      this.wallColor = this.dataset.wallColor;
      const entry = WALL_COLORS.find(c => c.hex === this.wallColor);
      if (entry) this.wallLight = entry.light;
    }

    if (!this.cornerCrop && !this.dataset.aiRoomFit) this.setupWallPicker();
    this.loadAssets();

    // Listen for wall color changes from other instances
    this._onWallSync = (e) => {
      if (e.detail?.hex && e.detail.hex !== this.wallColor) {
        this._applyWallColor(e.detail.hex, false);
      }
    };
    document.addEventListener('wall-color-change', this._onWallSync);
  }

  disconnectedCallback() {
    _dequeueRender(this);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    clearTimeout(this._resizeDebounce);
    if (this._scheduleRafId) {
      cancelAnimationFrame(this._scheduleRafId);
      this._scheduleRafId = null;
    }
    if (this._wallPills) {
      this._wallPills.remove();
      this._wallPills = null;
      this._wallFab = null;
      this._pills = null;
      this._pickerOpen = false;
    }
    if (this._onWallSync) {
      document.removeEventListener('wall-color-change', this._onWallSync);
      this._onWallSync = null;
    }
  }

  parseData() {
    this.artworkUrl = this.dataset.artworkUrl || '';
    this.artworkTitle = this.dataset.artworkTitle || 'Artwork';
    this.positionId = this.dataset.positionId || '';
    this.frameColor = this.dataset.frameColor || '#2d2d2d';
    this.mouldingTile = this.dataset.mouldingTile || '';
    this.mouldingWidth = parseFloat(this.dataset.mouldingWidth) || 15;
    // Fixed 0.5 in (12.7 mm) rebate per side — applied to all frames so
    // every 2D render matches the 3D preview's physical dimensions. Not
    // data-driven; the product catalogue treats the overlap as a constant.
    this.rebateWidth = 12.7;
    this.borderWidth = parseFloat(this.dataset.borderWidth) || 0;
    this.borderOption = this.dataset.borderOption || 'no-border';
    this.mountColor = this.dataset.mountColor || '#fafaf8';
    this.mountColorInner = this.dataset.mountColorInner || this.mountColor;
    this.hasFrame = this.dataset.hasFrame !== 'false';
    // Artwork physical width in inches (from size option) — used for correct frame/mat proportions
    this.artworkWidth = parseFloat(this.dataset.artworkWidth) || 16;
    this.artworkAspect = parseFloat(this.dataset.artworkAspect) || 1;
    // Frame depth (0-1) controls bevel, inner shadows, shading intensity
    this.frameDepth = parseFloat(this.dataset.frameDepth) || 0.5;
    const ppRaw = this.dataset.profilePoints;
    if (ppRaw) { try { this.profilePoints = JSON.parse(ppRaw); } catch(e) { this.profilePoints = null; } }
    else { this.profilePoints = null; }
    // Glass reflection opacity (0 = none)
    this.glassReflection = parseFloat(this.dataset.glassReflection) || 0.06;
    this.wallTextureUrl = this.dataset.wallTexture || '';
    this.roomLightingUrls = {
      portrait: this.dataset.roomLightingPortrait || '',
      landscape: this.dataset.roomLightingLandscape || '',
      square: this.dataset.roomLightingSquare || '',
    };
    this.roomLightingMaps = {};
    this.productType = this.dataset.productType || 'art_print';
    this.frameFraction = parseFloat(this.dataset.frameFraction) || 0.80;
    this.frameOffsetY = parseFloat(this.dataset.frameOffsetY) || 0; // fraction of canvas, negative = up
    this.cornerCrop = this.dataset.cornerCrop === 'true';
    // Keep the shared corner-crop bitmap cache coherent with the artwork this
    // tile renders. Tiles built directly from HTML (row cards, sheet cards)
    // never go through setArtwork(), so reconcile here too — otherwise a tile
    // carrying a fresh artwork could blit a stale cached bitmap keyed only by
    // frame colour / border.
    if (this.cornerCrop) _setCornerArtwork(this.artworkUrl);
  }

  async loadAssets() {
    // Corner-crop cache hit: paint the stored bitmap immediately, before any async work.
    // Assets still load in the background so wall-color re-renders have everything they need.
    let _drawnFromCache = false;
    if (this.cornerCrop) {
      const cached = _cornerBitmapCache.get(_cornerCacheKey(this));
      if (cached) {
        this.canvas.width = cached.width;
        this.canvas.height = cached.height;
        this.ctx.drawImage(cached, 0, 0);
        this.canvas.style.opacity = '1';
        _drawnFromCache = true;
      }
    }

    const promises = [];
    if (this.artworkUrl) {
      promises.push(loadImage(this.artworkUrl).then(img => { this.artworkImage = img; }));
    }
    if (this.mouldingTile) {
      promises.push(loadImage(this.mouldingTile).then(img => { this.mouldingTexture = img; }));
    }
    if (this.wallTextureUrl) {
      promises.push(loadImage(this.wallTextureUrl).then(img => { this.wallTextureTile = img; }));
    }
    for (const [orient, url] of Object.entries(this.roomLightingUrls)) {
      if (url) {
        promises.push(loadImage(url).then(img => { this.roomLightingMaps[orient] = img; }));
      }
    }
    await Promise.all(promises);
    this._assetsLoaded = true;
    if (this.cornerCrop) {
      // Only enqueue a full render if we didn't already draw from cache.
      // If we did, assets are now available for future re-renders (e.g. wall color change)
      // but the thumbnail is already correct — no stagger delay needed.
      if (!_drawnFromCache) {
        _enqueueRender(this);
      }
    } else {
      this.render();
      this.setupResizeObserver();
    }

    // Retry artwork load once if it failed (transient CDN/network errors)
    if (!this.artworkImage && this.artworkUrl) {
      loadImage(this.artworkUrl).then(img => {
        if (img) {
          this.artworkImage = img;
          this.scheduleRender();
        }
      });
    }
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver((entries) => {
      // Debounce resize renders — during pinch-to-zoom the element resizes every frame.
      // Wait for the gesture to settle before re-rendering to avoid GPU memory thrashing
      // and repeated 100ms+ renders that crash mobile browsers.
      clearTimeout(this._resizeDebounce);
      this._resizeDebounce = setTimeout(() => {
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        if (size > 0 && Math.abs(size - this._lastRenderedSize) < 1) {
          return;
        }
        this.render();
      }, 150);
    });
    this.resizeObserver.observe(this);
  }

  setupWallPicker() {
    const SVG_ROLLER = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="21" viewBox="0 0 16 21" fill="currentColor"><path d="M11 1.96875H2C1.725 1.96875 1.5 2.26406 1.5 2.625V5.25C1.5 5.61094 1.725 5.90625 2 5.90625H11C11.275 5.90625 11.5 5.61094 11.5 5.25V2.625C11.5 2.26406 11.275 1.96875 11 1.96875ZM2 0H11C12.1031 0 13 1.17715 13 2.625V2.95312V4.92188V5.25C13 6.69785 12.1031 7.875 11 7.875H2C0.896875 7.875 0 6.69785 0 5.25V2.625C0 1.17715 0.896875 0 2 0ZM5 14.4375C5 13.7115 5.44688 13.125 6 13.125H6.25V12.4688C6.25 10.8363 7.25625 9.51562 8.5 9.51562H13.25C13.9406 9.51562 14.5 8.78145 14.5 7.875V5.90625C14.5 5.47969 14.2906 5.11465 14 4.9793V2.96953C15.125 3.13359 16 4.38457 16 5.90625V7.875C16 9.86836 14.7688 11.4844 13.25 11.4844H8.5C8.08438 11.4844 7.75 11.9232 7.75 12.4688V13.125H8C8.55313 13.125 9 13.7115 9 14.4375V19.6875C9 20.4135 8.55313 21 8 21H6C5.44688 21 5 20.4135 5 19.6875V14.4375Z"/></svg>';
    const SVG_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    const container = document.createElement('div');
    container.className = 'frame-mock-wall-picker';
    container.style.cssText = 'position:absolute;bottom:12px;right:12px;z-index:2;width:44px;height:44px;';

    // FAB button
    const fab = document.createElement('button');
    fab.className = 'frame-mock-wall-fab';
    fab.setAttribute('type', 'button');
    fab.setAttribute('aria-label', this.dataset.i18nWallPicker || 'Wall color picker');
    fab.style.cssText = 'position:absolute;bottom:0;right:0;width:44px;height:44px;border-radius:50%;border:none;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.18);z-index:1;color:#555;transition:transform 0.25s ease;';
    fab.innerHTML = SVG_ROLLER;
    container.appendChild(fab);

    // Swatch pills
    const pills = [];
    const count = WALL_COLORS.length;
    const radius = 72;
    const arcStart = 180 * (Math.PI / 180);
    const arcEnd = 280 * (Math.PI / 180);

    WALL_COLORS.forEach((c, i) => {
      const btn = document.createElement('button');
      btn.className = 'frame-mock-wall-pill' + (c.hex === this.wallColor ? ' frame-mock-wall-pill--active' : '');
      btn.dataset.hex = c.hex;
      const wallLabel = (this.dataset.i18nWallColor || '__NAME__ wall').replace('__NAME__', c.name);
      btn.setAttribute('aria-label', wallLabel);
      btn.setAttribute('type', 'button');
      btn.style.cssText = `position:absolute;bottom:4px;right:4px;width:36px;height:36px;border-radius:50%;cursor:pointer;padding:0;background:${c.hex};border:2.5px solid ${c.hex === this.wallColor ? '#6366f1' : 'transparent'};box-shadow:inset 0 1px 2px rgba(255,255,255,0.3),0 1px 3px rgba(0,0,0,0.2);transform:scale(0);opacity:0;z-index:0;transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1),opacity 0.2s ease,border-color 0.15s;transition-delay:0ms;`;

      // Pre-compute expanded position
      const angle = arcStart + (arcEnd - arcStart) * (i / (count - 1));
      btn._expandX = Math.cos(angle) * radius;
      btn._expandY = Math.sin(angle) * radius;

      container.appendChild(btn);
      pills.push(btn);
    });

    // Toggle picker open/close
    fab.addEventListener('click', (e) => {
      e.stopPropagation();
      this._pickerOpen = !this._pickerOpen;
      if (this._pickerOpen) {
        fab.innerHTML = SVG_CLOSE;
        fab.style.transform = 'rotate(180deg)';
        pills.forEach((btn, i) => {
          btn.style.transitionDelay = (i * 25) + 'ms';
          btn.style.transform = `translate(${btn._expandX}px, ${btn._expandY}px) scale(1)`;
          btn.style.opacity = '1';
        });
      } else {
        fab.innerHTML = SVG_ROLLER;
        fab.style.transform = 'rotate(0deg)';
        pills.forEach((btn, i) => {
          btn.style.transitionDelay = ((count - 1 - i) * 25) + 'ms';
          btn.style.transform = 'translate(0px, 0px) scale(0)';
          btn.style.opacity = '0';
        });
      }
    });

    // Swatch click
    container.addEventListener('click', (e) => {
      const btn = e.target.closest('.frame-mock-wall-pill');
      if (!btn || !btn.dataset.hex) return;
      this.handleWallColorChange(btn.dataset.hex);
      // Auto-close palette
      setTimeout(() => {
        if (this._pickerOpen) fab.click();
      }, 150);
    });

    this._wallPills = container;
    this._wallFab = fab;
    this._pills = pills;
    this.appendChild(container);
  }

  _getSavedWallColor() {
    try {
      const raw = localStorage.getItem(WALL_COLOR_STORAGE_KEY);
      if (!raw) return null;
      let hex = raw;
      // Bundle stores JSON format: {"handle":"...","hex":"#..."}
      try {
        const data = JSON.parse(raw);
        if (data && data.hex) hex = data.hex;
      } catch {
        // Legacy raw hex string — use as-is
      }
      const entry = WALL_COLORS.find(c => c.hex === hex);
      if (entry) return entry;
    } catch (e) { /* localStorage unavailable */ }
    return null;
  }

  _applyWallColor(hex, broadcast) {
    if (hex === this.wallColor) return;
    this.wallColor = hex;
    const entry = WALL_COLORS.find(c => c.hex === hex);
    if (entry) this.wallLight = entry.light;
    _invalidateWallCache();

    // Update pill active states
    const pills = this._wallPills?.querySelectorAll('.frame-mock-wall-pill');
    if (pills) {
      pills.forEach((btn) => {
        const isActive = btn.dataset.hex === hex;
        btn.classList.toggle('frame-mock-wall-pill--active', isActive);
        btn.style.borderColor = isActive ? '#6366f1' : 'transparent';
      });
    }

    if (this._assetsLoaded) this.scheduleRender();

    if (broadcast) {
      try { localStorage.setItem(WALL_COLOR_STORAGE_KEY, JSON.stringify({ handle: 'custom', hex })); } catch (e) {}
      document.dispatchEvent(new CustomEvent('wall-color-change', { detail: { hex } }));
    }
  }

  handleWallColorChange(hex) {
    this._applyWallColor(hex, true);
  }

  updateConfig(config) {
    let needsTextureReload = false;

    if (config.frameColor !== undefined) {
      this.frameColor = config.frameColor;
      this.dataset.frameColor = config.frameColor;
    }
    if (config.mouldingTile !== undefined && config.mouldingTile !== this.mouldingTile) {
      this.mouldingTile = config.mouldingTile;
      this.dataset.mouldingTile = config.mouldingTile;
      needsTextureReload = true;
    }
    if (config.mouldingWidth !== undefined) {
      this.mouldingWidth = parseFloat(config.mouldingWidth) || 15;
      this.dataset.mouldingWidth = String(this.mouldingWidth);
    }
    if (config.borderWidth !== undefined) {
      this.borderWidth = parseFloat(config.borderWidth) || 0;
      this.dataset.borderWidth = String(this.borderWidth);
    }
    if (config.borderOption !== undefined) {
      this.borderOption = config.borderOption;
      this.dataset.borderOption = config.borderOption;
    }
    if (config.mountColor !== undefined) {
      this.mountColor = config.mountColor;
      this.dataset.mountColor = config.mountColor;
    }
    if (config.mountColorInner !== undefined) {
      this.mountColorInner = config.mountColorInner;
      this.dataset.mountColorInner = config.mountColorInner;
    }
    if (config.hasFrame !== undefined) {
      this.hasFrame = config.hasFrame !== false && config.hasFrame !== 'false';
      this.dataset.hasFrame = String(this.hasFrame);
    }
    if (config.artworkWidth !== undefined && !this.cornerCrop) {
      this.artworkWidth = parseFloat(config.artworkWidth) || 16;
      this.dataset.artworkWidth = String(this.artworkWidth);
    }
    if (config.artworkAspect !== undefined && !this.cornerCrop) {
      this.artworkAspect = parseFloat(config.artworkAspect) || 1;
      this.dataset.artworkAspect = String(this.artworkAspect);
    }
    if (config.productType !== undefined) {
      this.productType = config.productType;
      this.dataset.productType = config.productType;
    }
    if (config.frameFraction !== undefined) {
      this.frameFraction = parseFloat(config.frameFraction) || 0.80;
    }
    if (config.frameOffsetY !== undefined) {
      this.frameOffsetY = parseFloat(config.frameOffsetY) || 0;
    }

    if (needsTextureReload) {
      // Clear stale texture immediately so any intermediate render uses the
      // solid-color fallback (with correct frameColor) instead of tiling
      // the old texture.  For cached images the .then() microtask restores
      // it before any macrotask-driven render can fire.
      this.mouldingTexture = null;
      if (this.mouldingTile) {
        loadImage(this.mouldingTile).then(img => {
          this.mouldingTexture = img;
          this.scheduleRender();
        });
      } else {
        this.scheduleRender();
      }
    } else {
      this.scheduleRender();
    }
  }

  scheduleRender() {
    if (this.cornerCrop) {
      // Skip rendering entirely if the element is not visible (e.g. in a closed dialog).
      // Mark dirty so it renders when it becomes visible again.
      if (!this._isVisible()) {
        this._dirty = true;
        return;
      }
      // Render directly for instant feedback (wall cache makes this fast)
      this.render();
    } else {
      // Defer to next frame so UI updates (e.g. active swatch) paint before the heavy render
      if (!this._scheduleRafId) {
        this._scheduleRafId = requestAnimationFrame(() => {
          this._scheduleRafId = null;
          this.render();
        });
      }
    }
  }

  /**
   * Swap the artwork image after initial render.
   *
   * Note: `artworkAspect` represents the *paper* aspect (drives frame /
   * mat layout), not the photo's. drawImageCover uses the image's native
   * aspect for the cover-fit scaling — no need to overwrite artworkAspect
   * from the photo. Only set it when the caller explicitly passes an
   * aspect (gallery-wall-style frame-fits-art flows); otherwise leave
   * the paper aspect alone (ReadyMade frame PDP-style fixed-paper flows).
   *
   * @param {string} url  - Object URL, data URL, or CDN URL of the new artwork
   * @param {number} [aspect] - Paper aspect override. Omit to preserve the
   *                             currently-set paper aspect.
   */
  async setArtwork(url, aspect) {
    // Corner-crop tiles share a bitmap cache that ignores the artwork URL, so a
    // changed artwork must invalidate it or render() will serve the old image.
    if (this.cornerCrop) _setCornerArtwork(url);
    this.artworkUrl = url;
    this.dataset.artworkUrl = url;
    const img = await loadImage(url);
    if (!img) return;
    this.artworkImage = img;
    if (aspect != null) {
      this.artworkAspect = aspect;
      this.dataset.artworkAspect = String(this.artworkAspect);
    }
    this.scheduleRender();
  }

  /** Flush dirty state — call when a hidden container becomes visible. */
  flushIfDirty() {
    if (this._dirty) {
      this._dirty = false;
      this.scheduleRender();
    }
  }

  _isVisible() {
    // Fast check: offsetParent is null for display:none ancestors (except fixed/body)
    return this.offsetParent !== null || this.getClientRects().length > 0;
  }

  render() {
    if (!this.canvas || !this.ctx || !this._assetsLoaded) return;

    // Corner-crop cache hit: blit the stored bitmap and skip the full render.
    if (this.cornerCrop) {
      const cached = _cornerBitmapCache.get(_cornerCacheKey(this));
      if (cached) {
        this.canvas.width = cached.width;
        this.canvas.height = cached.height;
        this.ctx.drawImage(cached, 0, 0);
        if (!this._placeholderRemoved) {
          this._placeholderRemoved = true;
          this.canvas.style.opacity = '1';
          const ph = this.querySelector('.frame-mock-lcp-placeholder');
          if (ph) ph.remove();
        }
        return;
      }
    }

    const rect = this.getBoundingClientRect();
    const containerSize = Math.max(rect.width, rect.height) || 400;
    // Track last rendered size to prevent unnecessary re-renders
    this._lastRenderedSize = containerSize;
    // Cap DPR — canvas renders don't benefit from 3× like photos do.
    // Hero: 2× (retina quality). Thumbnails: 1.5× (avoids huge offscreen with 4× crop).
    const maxDpr = this.cornerCrop ? (parseFloat(this.dataset.cornerCropDpr) || 1.5) : 2;
    const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
    const displaySize = Math.round(containerSize * dpr);

    this.canvas.width = displaySize;
    this.canvas.height = displaySize;
    if (!this.cornerCrop) {
      this.canvas.style.width = containerSize + 'px';
      this.canvas.style.height = containerSize + 'px';
    }

    // Corner-crop mode: render at 4× into shared offscreen buffer, then crop bottom-left.
    // Shared buffer avoids 12 × 18MB = 216MB GPU memory (crashes mobile).
    const cropScale = this.cornerCrop ? 4.0 : 1;
    const size = displaySize * cropScale;

    let ctx;
    if (this.cornerCrop) {
      const shared = _getSharedOffscreen(size);
      ctx = shared.ctx;
    } else {
      ctx = this.ctx;
    }

    // Select orientation-specific lighting map based on artwork aspect ratio
    const orientAspect = this.artworkAspect || 1;
    const orientKey = orientAspect > 1.05 ? 'landscape' : orientAspect < 0.95 ? 'portrait' : 'square';
    const lightingMap = this.roomLightingMaps[orientKey] || this.roomLightingMaps.portrait || null;

    // Textured wall background — corner-crop blits from shared cache (rendered once for all tiles)
    const lt = this.wallLight;
    const thumbnail = this.cornerCrop;
    if (thumbnail) {
      const wallBg = _getWallCache(size, this.wallColor, this.wallTextureTile, lt, lightingMap, orientKey);
      ctx.drawImage(wallBg, 0, 0);
    } else {
      renderWallTexture(ctx, size, size, this.wallColor, this.wallTextureTile, lt, lightingMap, this.frameFraction, this.frameOffsetY);
    }

    // Physical dimensions in inches.
    const MM_TO_INCHES = 0.03937;
    const CM_TO_INCHES = 0.3937;
    const aspect = this.artworkAspect
      || (this.artworkImage ? (this.artworkImage.width / this.artworkImage.height) : 1);
    const paperWidthInches = this.artworkWidth || 16;
    const paperHeightInches = paperWidthInches / aspect;
    const isCanvas = this.productType === 'canvas';

    if (isCanvas) {
      this._renderCanvas(ctx, size, lt, aspect, paperWidthInches, paperHeightInches, MM_TO_INCHES, thumbnail);
    } else {
      this._renderArtPrint(ctx, size, lt, aspect, paperWidthInches, paperHeightInches, MM_TO_INCHES, CM_TO_INCHES, thumbnail);
    }

    // Corner-crop: blit region around frame's bottom-left corner to display canvas
    if (this.cornerCrop && this._frameRect) {
      const fr = this._frameRect;
      // Position crop so frame corner is ~20% from left and ~80% from top
      const sx = Math.max(0, Math.min(fr.x - displaySize * 0.20, size - displaySize));
      const sy = Math.max(0, Math.min(fr.y + fr.h - displaySize * 0.80, size - displaySize));
      this.ctx.drawImage(_sharedOffscreen, sx, sy, displaySize, displaySize, 0, 0, displaySize, displaySize);

      // Store in bitmap cache for reuse by other tiles with the same frame color + border option.
      const key = _cornerCacheKey(this);
      if (!_cornerBitmapCache.has(key)) {
        const snap = document.createElement('canvas');
        snap.width = this.canvas.width;
        snap.height = this.canvas.height;
        snap.getContext('2d').drawImage(this.canvas, 0, 0);
        _cornerBitmapCache.set(key, snap);
      }
    }

    // Reveal canvas and remove LCP placeholder after first render
    if (!this._placeholderRemoved) {
      this._placeholderRemoved = true;
      this.canvas.style.opacity = '1';
      const placeholder = this.querySelector('.frame-mock-lcp-placeholder');
      if (placeholder) placeholder.remove();
    }

    // Blit to any registered thumbnail canvases — single drawImage, no re-render
    if (this._thumbnailTargets.length) {
      for (const thumb of this._thumbnailTargets) {
        const tctx = thumb.getContext('2d');
        tctx.clearRect(0, 0, thumb.width, thumb.height);
        tctx.drawImage(this.canvas, 0, 0, thumb.width, thumb.height);
      }
    }

    if (!this.cornerCrop) {
      this.dispatchEvent(new CustomEvent('rmf:rendered', { bubbles: false }));
    }
  }

  /** Returns pixel-accurate layer positions for measure-line overlays, or null before first render. */
  getRenderLayout() {
    if (!this._frameRect || !this._renderLayout) return null;
    return {
      bufferSize:    this._renderLayout.bufferSize,
      frameRect:     this._frameRect,
      frameWidthPx:  this._renderLayout.frameWidthPx,
      matWidthPx:    this._renderLayout.matWidthPx,
      overhangPx:    this._renderLayout.overhangPx ?? this._renderLayout.frameWidthPx,
      rebatePx:      this._renderLayout.rebatePx   ?? 0,
    };
  }

  _renderArtPrint(ctx, size, lt, aspect, paperWidthInches, paperHeightInches, MM_TO_INCHES, CM_TO_INCHES, thumbnail) {
    const frameWidthInches = this.hasFrame ? this.mouldingWidth * MM_TO_INCHES : 0;
    // Outer size = paper + 2 × (moulding − rebate). The rebate is how much
    // of the paper sits UNDER the frame's inner lip on each side. Without
    // this subtraction the 2D mock renders frames ~10–15% larger than the
    // 3D preview (which does the same math) and ~3% larger than the
    // physical Horizon spec (e.g. 20×30 paper + 1.6" moulding = 22.7"
    // overall, not 23.2").
    const rebateInches = this.hasFrame ? this.rebateWidth * MM_TO_INCHES : 0;
    const overhangInches = Math.max(0, frameWidthInches - rebateInches);
    const matWidthInches = (this.borderOption !== 'no-border' && this.borderWidth > 0)
      ? this.borderWidth * CM_TO_INCHES : 0;
    const totalPhysicalW = paperWidthInches + 2 * overhangInches;
    const totalPhysicalH = paperHeightInches + 2 * overhangInches;

    const frameFraction = this.frameFraction;
    const pxPerInch = Math.min(
      (size * frameFraction) / totalPhysicalW,
      (size * frameFraction) / totalPhysicalH
    );

    const totalW = totalPhysicalW * pxPerInch;
    const totalH = totalPhysicalH * pxPerInch;
    const frameWidthPx = frameWidthInches * pxPerInch;
    const matWidthPx = matWidthInches * pxPerInch;

    const offsetX = (size - totalW) / 2;
    const offsetY = (size - totalH) / 2 + this.frameOffsetY * size;
    this._frameRect = { x: offsetX, y: offsetY, w: totalW, h: totalH };
    if (!thumbnail) {
      // overhangPx = how far the moulding extends BEYOND the paper on each
      // side. The paper edge sits `rebatePx` inside the outer moulding edge
      // (i.e. `overhangPx` from the outer edge, underneath the rebate lip).
      const overhangPx = Math.max(0, frameWidthInches - rebateInches) * pxPerInch;
      const rebatePx   = rebateInches * pxPerInch;
      this._renderLayout = { bufferSize: size, frameWidthPx, matWidthPx, overhangPx, rebatePx };
    }

    renderArtPrintFrame(ctx, {
      frame: this.hasFrame ? { x: offsetX, y: offsetY, w: totalW, h: totalH } : null,
      opening: { x: offsetX + frameWidthPx, y: offsetY + frameWidthPx,
                 w: totalW - frameWidthPx * 2, h: totalH - frameWidthPx * 2 },
      artwork: { x: offsetX + frameWidthPx + matWidthPx, y: offsetY + frameWidthPx + matWidthPx,
                 w: totalW - (frameWidthPx + matWidthPx) * 2, h: totalH - (frameWidthPx + matWidthPx) * 2 },
      frameWidthPx, matWidthPx, hasFrame: this.hasFrame,
      frameColor: this.frameColor, frameDepth: this.frameDepth,
      profilePoints: this.profilePoints || null,
      mouldingTexture: this.mouldingTexture,
      artworkImage: this.artworkImage,
      glassReflection: this.glassReflection,
      borderOption: this.borderOption, pxPerInch,
      matColor: this.mountColor,
      matColorInner: this.mountColorInner,
      shadow: { wallColor: this.wallColor, shadowDir: lt.shadowDir },
      pixelSnap: true,
      skipShadow: thumbnail,
    });

    // Environment effects (gallery-wall specific, not shared)
    if (!thumbnail && this.hasFrame && frameWidthPx > 0) {
      renderAmbientBleed(ctx, offsetX, offsetY, totalW, totalH, this.wallColor, frameWidthPx, this.frameColor);
    }
    if (!thumbnail) {
      renderSpotlightWash(ctx, size, size, offsetX + totalW / 2, offsetY + totalH / 2, lt);
    }
  }

  _renderCanvas(ctx, size, lt, aspect, paperWidthInches, paperHeightInches, MM_TO_INCHES, thumbnail) {
    const CANVAS_FRAME_WIDTH_MM = 12;
    const CANVAS_FRAME_DEPTH = 0.85;
    const CANVAS_FLOAT_GAP_MM = 8;
    const CANVAS_EDGE_MM = 5;

    const canvasEdgeInches = CANVAS_EDGE_MM * MM_TO_INCHES;
    const floatGapInches = this.hasFrame ? CANVAS_FLOAT_GAP_MM * MM_TO_INCHES : 0;
    const frameWidthInches = this.hasFrame ? CANVAS_FRAME_WIDTH_MM * MM_TO_INCHES : 0;

    const totalPhysicalW = paperWidthInches + 2 * (canvasEdgeInches + floatGapInches + frameWidthInches);
    const totalPhysicalH = paperHeightInches + 2 * (canvasEdgeInches + floatGapInches + frameWidthInches);

    const frameFraction = this.frameFraction;
    const pxPerInch = Math.min(
      (size * frameFraction) / totalPhysicalW,
      (size * frameFraction) / totalPhysicalH
    );

    const totalW = totalPhysicalW * pxPerInch;
    const totalH = totalPhysicalH * pxPerInch;
    const frameWidthPx = frameWidthInches * pxPerInch;
    const floatGapPx = floatGapInches * pxPerInch;
    const canvasEdgePx = canvasEdgeInches * pxPerInch;

    const offsetX = (size - totalW) / 2;
    const offsetY = (size - totalH) / 2 + this.frameOffsetY * size;
    this._frameRect = { x: offsetX, y: offsetY, w: totalW, h: totalH };

    const edgeX = offsetX + frameWidthPx + floatGapPx;
    const edgeY = offsetY + frameWidthPx + floatGapPx;
    const edgeW = totalW - (frameWidthPx + floatGapPx) * 2;
    const edgeH = totalH - (frameWidthPx + floatGapPx) * 2;

    renderCanvasProductFrame(ctx, {
      outer: { x: offsetX, y: offsetY, w: totalW, h: totalH },
      edge: { x: edgeX, y: edgeY, w: edgeW, h: edgeH },
      frameWidthPx, floatGapPx, canvasEdgePx, hasFrame: this.hasFrame,
      frameColor: this.frameColor, frameDepth: CANVAS_FRAME_DEPTH,
      mouldingTexture: this.mouldingTexture,
      artworkImage: this.artworkImage,
      shadow: { wallColor: this.wallColor, shadowDir: lt.shadowDir },
      skipShadow: thumbnail,
    });

    // Environment effects (gallery-wall specific, not shared)
    if (!thumbnail) {
      if (this.hasFrame) {
        renderAmbientBleed(ctx, offsetX, offsetY, totalW, totalH, this.wallColor, frameWidthPx, this.frameColor);
      } else {
        renderAmbientBleed(ctx, offsetX, offsetY, totalW, totalH, this.wallColor, canvasEdgePx);
      }
      renderSpotlightWash(ctx, size, size, offsetX + totalW / 2, offsetY + totalH / 2, lt);
    }
  }
}

if (!customElements.get('gallery-wall-frame-mock')) {
  customElements.define('gallery-wall-frame-mock', GalleryWallFrameMock);
}
